// @refresh reset
// ─────────────────────────────────────────────────────────────────────────────
// THE SCOPE CONFIRMATION EMAIL
//
// Erez-locked 2026-07-27: a NEW STEP in the process, between scoping and
// pricing. Before any work order or price goes out, the client confirms the
// feature list in writing. We assume NOTHING.
//
// Three asks per feature, and only three:
//   1. do you want it
//   2. is our description right — and how do you picture it, in your words
//   3. how many people would actually USE it
//
// Deliberately absent: every user count we estimated. Those were our guesses
// and putting them in the email would anchor the answer, which defeats the
// entire point of asking. The 🔴 numbers in the pricing sheet get REPLACED by
// what comes back, not confirmed by it.
//
// This is now a MIRROR of the copy Erez edited in his own mail client. He owns
// the wording; the board just has to match it. Still nothing sent from here.
//
// Owner agent: Hank.
// ─────────────────────────────────────────────────────────────────────────────

import { useState } from "react";
import { N, NFONT, NMONO, R, R_CTL } from "./notion";

const F = NFONT;
const MONO = NMONO;

const SUBJECT = "Confirming the feature list before we scope";

/** THE BODY IS EREZ'S OWN COPY, pasted verbatim 2026-07-27 after he edited it
 *  in the mail client. His version is the source of truth — it deliberately
 *  restores feature 10, the word "cohort", and phrasing I had trimmed. Do NOT
 *  re-apply the earlier edits; if it reads differently to a previous note in
 *  this file, HIS text wins. **bold** renders, and is stripped for the copy. */
const BODY = `Hi Daniel and John,

Great to meet you last week. That conversation gave us a lot — we came away with a picture of how the business runs and where the friction is.

Before we define the scope, we want to ensure we are building the right list. Below is what we think came out of that conversation.

For each one, three quick things:

1. Do you want it? (yes / no / later)
2. Is our description right — and how do you picture it working, in your own words?
3. How many people would actually use it?

**1. Definitions catalogue**
One agreed definition of a job, a lead, a source and a market, applied everywhere, so two reports stop disagreeing.

**2. Reporting you can trust**
Your numbers by market, channel and service — with period and cohort never shown as though they are the same thing.

**3. Ask questions in plain language**
Ask "where are we weakest this month" or "show me ROAS by market" and get an answer, with the underlying numbers behind it.

**4. Self-healing data connections**
When a system renames a field or changes a column, it is detected and repaired automatically — and you are told it happened.

**5. Speed to lead, in-house**
A new lead is contacted immediately and routed to the right person, replacing what you use for this today.

**6. Scheduling and capacity**
The real availability of each crew, so jobs are not double-booked, lost, or promised for a slot that does not exist.

**7. Running late**
When a job is going to overrun, the next customer is told and the rest of the day is adjusted.

**8. Commissions and payroll**
The cycle calculated automatically, the exceptions flagged for a person to check, approved in one place.

**9. Call centre visibility**
Handle time, abandoned calls, answer rates. Honestly, this one depends on what your phone system will give us — so it would help to know who we can speak to about your CallRail setup.

**10. Permissions and a safety gate**
Control over what the system may do on its own and what needs a person to approve it, plus access per person.

Please provide your feedback and confirmation on these features. If anything is missing, please add it as a new line in your response.

Thanks,

--
Erez Fern
Founder & CEO
Tailormind`;

/** Renders **bold** and leaves everything else alone. */
function render(text: string) {
  return text.split("\n").map((line, i) => {
    if (line.trim() === "") return <div key={i} style={{ height: 11 }} />;
    const parts = line.split(/(\*\*[^*]+\*\*)/g);
    return (
      <div key={i} style={{ fontSize: 16, lineHeight: 1.5, color: N.ink, marginBottom: 1 }}>
        {parts.map((p, j) =>
          p.startsWith("**") && p.endsWith("**") ? (
            <strong key={j} style={{ fontWeight: 600, color: N.ink }}>{p.slice(2, -2)}</strong>
          ) : (
            <span key={j}>{p}</span>
          ),
        )}
      </div>
    );
  });
}

export function AirDuctScopeEmail() {
  const [copied, setCopied] = useState<"yes" | "fail" | null>(null);

  const plain = `Subject: ${SUBJECT}\n\n${BODY.replace(/\*\*/g, "")}`;

  // navigator.clipboard is unavailable in insecure contexts and throws when the
  // document is not focused — both of which look exactly like a dead button.
  // Fall back to the textarea trick so Copy always does something.
  const copy = async () => {
    let ok = false;
    try {
      await navigator.clipboard.writeText(plain);
      ok = true;
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = plain;
        ta.style.cssText = "position:fixed;top:0;left:0;opacity:0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        ok = document.execCommand("copy");
        document.body.removeChild(ta);
      } catch {
        ok = false;
      }
    }
    setCopied(ok ? "yes" : "fail");
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    // See the note in calculator.tsx: without nowheel/nodrag/nopan the canvas
    // eats the click and the Copy button does nothing.
    <div
      className="nowheel nodrag nopan"
      data-zoom
      onPointerDown={(e) => e.stopPropagation()}
      style={{
        pointerEvents: "auto",
        width: 1000, background: N.bg, border: `1px solid ${N.line}`, borderRadius: R,
        fontFamily: F, boxSizing: "border-box", overflow: "hidden",
      }}
    >
      {/* status bar — this is the thing that stops it being sent by accident */}
      <div style={{
        margin: "22px 26px 0", background: N.yellowBg, borderRadius: R,
        padding: "12px 14px", display: "flex", alignItems: "center",
        justifyContent: "space-between", gap: 12,
      }}>
        <span style={{ fontSize: 14.5, color: N.ink, display: "flex", alignItems: "center", gap: 9 }}>
          <span style={{ fontSize: 16 }}>✉️</span>
          Erez's copy, mirrored from his email. The board is the mirror, not the master.
        </span>
        <button
          onClick={copy}
          style={{
            fontFamily: F, fontSize: 13, fontWeight: 500, cursor: "pointer",
            whiteSpace: "nowrap",
            color: copied ? "#FFFFFF" : N.ink2,
            background: copied === "yes" ? N.green : copied === "fail" ? N.red : N.bg,
            border: `1px solid ${copied ? "transparent" : N.lineStrong}`,
            borderRadius: R_CTL, padding: "5px 11px",
          }}
        >{copied === "yes" ? "Copied" : copied === "fail" ? "Select it manually" : "Copy email"}</button>
      </div>

      <div style={{ padding: "22px 26px 0" }}>
        <div style={{ fontSize: 12.5, color: N.ink3, letterSpacing: ".02em" }}>Subject</div>
        <div style={{ fontSize: 30, fontWeight: 700, color: N.ink, marginTop: 3, letterSpacing: "-.02em" }}>{SUBJECT}</div>
      </div>

      <div style={{ padding: "20px 26px 26px", userSelect: "text", cursor: "text" }}>{render(BODY)}</div>

      <div style={{
        margin: "0 26px 26px", background: N.grayBg, borderRadius: R,
        padding: "12px 14px", fontSize: 13.5, color: N.ink2, lineHeight: 1.55,
      }}>
        <b>Why no numbers in it:</b> every user count in the pricing sheet is our estimate. Putting them here would
        anchor the answer and we would just get our own guesses back. Their reply replaces those figures — it does not confirm them.
      </div>
    </div>
  );
}
